import { useDebounceFn, useThrottleFn, useLockFn } from 'ahooks';
import { environment } from '@gaodun.com/gtools';
import { user_info, upload_file } from '@/sub-luca-plugin/apis';
import { queryDomExample, eventBusExample } from './class-example';
import { IClientRectInfo } from '@/sub-luca-plugin/types';
import { DEFAULT_USER_AVATAR } from './config';
import { loadMicroApp } from 'qiankun';

export { useDebounceFn, useThrottleFn, useLockFn, environment };

export const getImgInfo = (url: string): Promise<{ width: number; height: number }> =>
    new Promise(resolve => {
        const img = new Image();
        img.onload = () => {
            resolve({ width: img.width, height: img.height });
        };
        img.onerror = () => {
            console.log('onerror');
            resolve({ width: 0, height: 0 });
        };
        img.src = url;
    });

export const LucaResizeObserver = ResizeObserver;

export const getClientRectInfo = (tag: any): Promise<IClientRectInfo> =>
    new Promise(resolve => {
        const dom = typeof tag === 'string' ? queryDomExample.querySelector(tag) : tag;
        const { clientWidth, clientHeight, scrollWidth, scrollHeight, scrollLeft, scrollTop } = dom;
        resolve({ clientWidth, clientHeight, scrollWidth, scrollHeight, scrollLeft, scrollTop });
    });

export const isWeApp = false;

export const previewPicture = (url: string) => {
    eventBusExample.publish('PicturePreview', { type: 'open', params: { url } });
};

export const uploadFile = async (file: File | string) => {
    if (typeof file === 'string') return '';
    try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('file_name', file.name);
        fd.append('item_name', 'luca');
        fd.append('file_type', 'file');
        const { result } = await upload_file(fd);
        return `https://${environment.getEnv()}simg01.gaodunwangxiao.com/${result}`;
    } catch (error) {
        return '';
    }
};

// 复制功能
export const copyToClipboard = async (text: string) => {
    try {
        await navigator.clipboard.writeText(text);
    } catch (error) {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
            document.execCommand('copy');
        } catch (error) {}
        document.body.removeChild(textArea);
    }
};

// 获取用户信息
export const getUserInfo = async (): Promise<any> =>
    new Promise(resolve => {
        user_info().then((res: any) => {
            const data = res.data;
            if (data) {
                resolve({
                    ...data,
                    headImgUrl: data.headImgUrl || DEFAULT_USER_AVATAR,
                });
            } else {
                resolve({});
            }
        });
    });

// 此方法在PC中是不存在的，只为了做导出的兼容
export const addEventCenterOfOnce = (callbacks: Record<string, (data: Record<string, any>) => void>) => {};

// 阻止背景滚动的次数，只有减为0才能滚动
let preventBodyScrollNum = 0;

export const preventBodyScroll = () => {
    preventBodyScrollNum++;
    document.body.style.overflow = 'hidden';
};

export const recoverBodyScroll = () => {
    if (preventBodyScrollNum > 0) {
        preventBodyScrollNum--;
    }
    if (preventBodyScrollNum === 0) {
        document.body.style.overflow = 'auto';
    }
};

class QiankunClass {
    loadMicroApp = loadMicroApp;
    // 主应用传入loadMicroApp时，使用主应用的
    setLoadMicroAppFn(callback?: any) {
        if (callback) this.loadMicroApp = callback;
    }
}

export const qiankunClassExample = new QiankunClass();

export const loadImg = (url: string): Promise<HTMLImageElement> =>
    new Promise(resolve => {
        const img = new Image();
        img.setAttribute('crossOrigin', 'anonymous');
        img.onload = () => resolve(img);
        img.src = url;
    });

// 获取图片的绘制信息
const getDrawInfo = async (urls: string[]) => {
    type TDrawInfo = {
        canvasWidth: number;
        canvasHeight: number;
        drawImageParamsList: Array<[HTMLImageElement, number, number, number, number]>;
    };
    const drawInfo: TDrawInfo = { canvasWidth: 0, canvasHeight: 0, drawImageParamsList: [] };
    const imgs = await Promise.all(urls.map(url => loadImg(url)));
    return imgs.reduce((info, img) => {
        info.drawImageParamsList.push([img, 0, info.canvasHeight, img.width, img.height]);
        info.canvasWidth = Math.max(info.canvasWidth, img.width);
        info.canvasHeight += img.height;
        return info;
    }, drawInfo);
};

// 合并压缩图片并上传
export const getCdnOfCompositePicture = (urls: string[]): Promise<string> =>
    new Promise(async resolve => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        const { canvasWidth, canvasHeight, drawImageParamsList } = await getDrawInfo(urls);
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        drawImageParamsList?.forEach(params => ctx.drawImage(...params));
        canvas.toBlob(
            async blob => {
                if (!blob) return;
                const file = new File([blob], 'image.png', { type: 'image/png', lastModified: Date.now() });
                console.log(`合成图大小：${Math.round((file.size / 1024 / 1024) * 10) / 10}M；`);
                const url = await uploadFile(file);
                resolve(url);
            },
            'image/jpg',
            0.7
        );
    });
